import React from 'react';
import PageShell from '../../components/PageShell';
import { Eyebrow, SectionHeading, StepChip, GradientButton, CTABanner } from '../../components/common/Primitives';
import { SP_ACCESS } from '../../lib/mockStudent';
import { Check, WifiOff, Wifi, RefreshCw, PlayCircle, Home } from 'lucide-react';
import LicenceFeeHighlight from '../../components/common/LicenceFeeHighlight';

export default function StudentAccess() {
  const d = SP_ACCESS;
  return (
    <PageShell>
      {/* HERO with video placeholder */}
      <section className="px-6 pt-12 pb-8">
        <div className="mx-auto max-w-[1240px]">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_1fr]">
            <div>
              <Eyebrow color="purple">{d.hero.eyebrow}</Eyebrow>
              <h1 className="mt-3 text-[40px] md:text-[54px] font-extrabold leading-[1.05] tracking-tight text-navy">{d.hero.title}</h1>
              <p className="mt-5 max-w-xl text-[15.5px] leading-relaxed text-ink-500">{d.hero.desc}</p>
              <div className="mt-6"><LicenceFeeHighlight text="No software licence fee through June 2027" /></div>
              <div className="mt-6 flex flex-wrap gap-3">
                <GradientButton to="/access-request">Request Access</GradientButton>
                <GradientButton to="/student-parent/experiences" variant="outline">Explore Experiences</GradientButton>
              </div>
              <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-purple-50 px-3 py-1.5 text-[11.5px] font-bold tracking-widest text-purple">{d.hero.pill}</div>
            </div>
            <div className="rounded-3xl border border-canvas-200 bg-gradient-to-br from-purple-50 via-white to-mint-50 p-6 md:p-8 shadow-soft">
              <div className="aspect-video rounded-2xl bg-brand-gradient grain relative flex items-center justify-center overflow-hidden">
                <span className="absolute top-3 left-3 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-bold tracking-widest text-navy">COMING SOON</span>
                <PlayCircle className="h-14 w-14 text-white/70" />
              </div>
              <div className="mt-4 text-[11.5px] font-bold tracking-widest text-purple">FAMILY ACCESS PREVIEW · COMING SOON</div>
              <p className="mt-1 text-[13px] text-ink-500">Family setup walkthrough coming soon.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAMILY PATHWAY (moved above SOFTWARE ACCESS) */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.familyPathway.eyebrow} title={d.familyPathway.title} desc={d.familyPathway.desc} />

          <div className="mt-8 rounded-3xl border border-canvas-200 bg-white p-6 md:p-8 shadow-soft">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.25fr]">
              <div className="relative overflow-hidden rounded-2xl shadow-card min-h-[240px]">
                <img
                  src="https://images.unsplash.com/photo-1698050683094-b7e0e57ea903?w=900&q=80&auto=format&fit=crop"
                  alt="Meta Quest 3S with comfortable head strap"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/25 to-transparent" />
                <div className="relative flex h-full flex-col justify-between p-6 text-white">
                  <Eyebrow color="cyan" className="!text-white/85">{d.familyPathway.subEyebrow}</Eyebrow>
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm ring-1 ring-white/25"><Home className="h-5 w-5 text-white" /></span>
                      <div className="text-[16px] font-bold">Meta Quest 3S · Comfort strap</div>
                    </div>
                    <div className="mt-2 text-[11.5px] tracking-widest text-white/85 font-bold">ONE LEARNER · ONE HEADSET · ONE ACCOUNT</div>
                  </div>
                </div>
              </div>
              <div>
                <div className="text-[19px] font-bold text-navy">{d.familyPathway.subTitle}</div>
                <p className="mt-2 text-[13.5px] text-ink-500">{d.familyPathway.subDesc}</p>
                <ul className="mt-5 space-y-2">
                  {d.familyPathway.subItems.map((s,i)=>(<li key={i} className="flex items-start gap-2 text-[13.5px] text-ink-700"><Check className="mt-0.5 h-4 w-4 text-mint" /> {s}</li>))}
                </ul>
                <div className="mt-5 flex flex-wrap gap-3">
                  <GradientButton to="/access-request">Request Access</GradientButton>
                  <GradientButton to="/student-parent/how-it-works" variant="outline">How Learning Works</GradientButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOFTWARE ACCESS — bullets instead of X marks */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.software.eyebrow} title={d.software.title} desc={d.software.desc} />
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="rounded-2xl border border-mint-50 bg-mint-50/40 p-6 shadow-soft">
              <div className="text-[13px] font-bold text-mint-600">{d.software.included.title}</div>
              <ul className="mt-4 space-y-2">{d.software.included.items.map((r,i)=>(<li key={i} className="flex items-start gap-2 text-[13.5px] text-ink-700"><Check className="mt-0.5 h-4 w-4 text-mint" /> {r}</li>))}</ul>
            </div>
            <div className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft">
              <div className="text-[13px] font-bold text-purple">{d.software.separate.title}</div>
              <ul className="mt-4 space-y-2">{d.software.separate.items.map((r,i)=>(<li key={i} className="flex items-start gap-2 text-[13.5px] text-ink-700"><span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-purple" /> {r}</li>))}</ul>
            </div>
          </div>
        </div>
      </section>

      {/* CONNECTIVITY */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.connectivity.eyebrow} title={d.connectivity.title} desc={d.connectivity.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {d.connectivity.items.map((c,i)=>{
              const Icon = i===0 ? WifiOff : i===1 ? Wifi : RefreshCw;
              const color = ['mint','royal','purple'][i];
              return (
                <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                  <div className="flex items-center gap-3"><span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl bg-${color}-50 text-${color}`}><Icon className="h-5 w-5" /></span><span className={`rounded-full bg-${color}-50 px-2.5 py-0.5 text-[10.5px] font-bold tracking-widest text-${color}-600`}>{c.badge}</span></div>
                  <div className="mt-4 text-[15px] font-bold text-navy">{c.title}</div>
                  <p className="mt-1 text-[13px] text-ink-500">{c.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* GETTING STARTED STEPS */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.steps.eyebrow} title={d.steps.title} desc={d.steps.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {d.steps.items.map((s,i)=>(<div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft flex items-start gap-3"><StepChip n={s.n} color={['purple','royal','cyan','mint'][i]} /><div><div className="text-[14.5px] font-bold text-navy">{s.title}</div><div className="mt-1 text-[13px] text-ink-500">{s.desc}</div></div></div>))}
          </div>
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="mx-auto max-w-[1240px]">
          <CTABanner title={d.cta.title} desc={d.cta.desc} primary={{ label: 'Request Access', to: '/access-request' }} />
        </div>
      </section>
    </PageShell>
  );
}
